import { API_BASE_URL } from "./apiConfig";

export const QUOTA_EXCEEDED_EVENT = "reopsai:quota-exceeded";
export const QUOTA_EXCEEDED_MESSAGE =
  "사용 가능한 토큰이 부족합니다. 관리자에게 문의하거나 토큰을 추가해 주세요.";
export const INDIVIDUAL_TRIAL_ENDED_MESSAGE =
  "무료 체험 토큰을 모두 사용했습니다. 이용 문의를 통해 계속 이용하실 수 있습니다.";
export const RATE_LIMIT_MESSAGE =
  "AI 요청이 일시적으로 많습니다. 잠시 후 다시 시도해 주세요.";

export const isQuotaExceededError = (error) => {
  if (Number(error?.status) === 402 || error?.code === "quota_exceeded") {
    return true;
  }
  const message = String(error?.message || error?.data?.message || "");
  return (
    message.includes("무료 체험 토큰")
    || message.includes("사용 가능한 토큰이 부족합니다")
  );
};

export const alertUnlessQuotaExceeded = (error, message) => {
  if (isQuotaExceededError(error)) return;
  const text = message || error?.message || "오류가 발생했습니다.";
  alert(text);
};

export const isRateLimitError = (error) => {
  if (Number(error?.status) === 429 || error?.code === "rate_limit_exceeded") {
    return true;
  }
  const message = String(error?.message || error?.data?.message || error?.data?.error || "").toLowerCase();
  return /(?:^|\s)429(?:\s|$)|rate limit|resource exhausted|too many requests|requests per minute|quota exceeded/.test(message);
};

const isPersonaApiPath = (path) => String(path || "").includes("/persona/");

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(value);
const READ_ONLY_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

const getStoredUser = () => {
  const rawUser = localStorage.getItem("user");
  if (!rawUser) return null;
  try {
    return JSON.parse(rawUser);
  } catch (error) {
    console.warn("Failed to parse stored user:", error);
    return null;
  }
};

export const apiUrl = (path) => {
  const value = String(path || "");
  if (isAbsoluteUrl(value)) return value;

  const base = API_BASE_URL.replace(/\/+$/, "");
  let endpoint = value.startsWith("/") ? value : `/${value}`;

  if (base.endsWith("/api") && endpoint.startsWith("/api/")) {
    endpoint = endpoint.slice(4);
  }

  return `${base}${endpoint}`;
};

export const getAuthHeaders = (overrides = {}, options = {}) => {
  const { json = true } = options;
  const user = getStoredUser();
  const token = localStorage.getItem("jwt");
  const headers = {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...overrides,
  };

  if (token && !("Authorization" in headers)) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (user?.id && !("X-User-ID" in headers)) {
    headers["X-User-ID"] = user.id.toString();
  }

  return headers;
};

export const parseApiError = async (response) => {
  const errorData = await response
    .json()
    .catch(() => ({ error: response.statusText }));
  const errorMessage = response.status === 402
    ? (errorData.message || QUOTA_EXCEEDED_MESSAGE)
    : response.status === 429
      ? RATE_LIMIT_MESSAGE
      : (errorData.message || errorData.error || errorData.msg || `API Error: ${response.statusText}`);

  if (response.status === 401) {
    console.warn("🔐 인증 만료 (401)");
    localStorage.removeItem("user");
    localStorage.removeItem("isLoggedIn");
  }

  if (response.status === 403 && errorMessage.includes("Password change required")) {
    if (!window.location.pathname.includes("/change-password")) {
      window.location.href = "/change-password?required=true";
    }
  }

  const error = new Error(errorMessage);
  error.status = response.status;
  error.code = response.status === 402
    ? "quota_exceeded"
    : response.status === 429
      ? "rate_limit_exceeded"
      : (errorData.status || errorData.code || errorData.error || errorData.msg);
  error.data = errorData;
  return error;
};

const notifyQuotaExceeded = async (response, method, path) => {
  if (response.status !== 402 || READ_ONLY_METHODS.has(method)) return;

  const errorData = await response.clone().json().catch(() => ({}));
  const message = errorData.message || QUOTA_EXCEEDED_MESSAGE;
  const trialEnded = Boolean(errorData.trial_ended);

  window.dispatchEvent(new CustomEvent(QUOTA_EXCEEDED_EVENT, {
    detail: {
      status: response.status,
      code: "quota_exceeded",
      message,
      trialEnded,
    },
  }));
};

export const handleApiResponse = async (response) => {
  if (!response.ok) {
    throw await parseApiError(response);
  }
  return response.json();
};

export const apiFetch = async (path, options = {}) => {
  const { headers: optionHeaders = {}, authHeaders = true, json, ...rest } = options;
  const hasFormData = typeof FormData !== "undefined" && rest.body instanceof FormData;
  const shouldUseJsonHeader = json ?? !hasFormData;
  const method = (rest.method || "GET").toUpperCase();
  const headers = authHeaders
    ? getAuthHeaders(optionHeaders, { json: shouldUseJsonHeader })
    : {
        ...(shouldUseJsonHeader ? { "Content-Type": "application/json" } : {}),
        ...optionHeaders,
      };

  const response = await fetch(apiUrl(path), {
    ...rest,
    headers,
    credentials: "include",
  });

  await notifyQuotaExceeded(response, method, path);
  return response;
};

export const apiJson = async (path, options = {}) => {
  const response = await apiFetch(path, options);
  return handleApiResponse(response);
};

export const apiStream = (path, options = {}) => apiFetch(path, options);

export const apiForm = (path, formData, options = {}) =>
  apiFetch(path, {
    ...options,
    method: options.method || "POST",
    body: formData,
    json: false,
  });
